import { memoize } from 'es-toolkit';

export const FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX = 'fc_override_';
export const FRONTEND_CONFIG_OVERRIDE_MAX_AGE_SECONDS = 86400;
export const FRONTEND_CONFIG_RESET_ALL_PARAM = 'fc_reset_all';

export type FrontendConfigType = 'Boolean' | 'String' | 'Number' | 'StringCsv' | 'NumberCsv';

export type FrontendConfigValue = boolean | string | number | Array<string> | Array<number>;

export type FrontendConfigOverride = {
  type: FrontendConfigType;
  name: string;
  value: FrontendConfigValue;
};

const COOKIE_TYPE_NAMES: Record<FrontendConfigType, string> = {
  Boolean: 'boolean',
  String: 'string',
  Number: 'number',
  StringCsv: 'string-csv',
  NumberCsv: 'number-csv',
};

const CONFIG_TYPES: Array<FrontendConfigType> = ['Boolean', 'StringCsv', 'NumberCsv', 'String', 'Number'];

export function getFrontendConfigOverrideCookieName(type: FrontendConfigType, name: string): string {
  return `${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${COOKIE_TYPE_NAMES[type]}_${name}`;
}

export function serializeFrontendConfigOverride(_type: FrontendConfigType, value: FrontendConfigValue): string {
  return encodeURIComponent(JSON.stringify(value));
}

function parseOverride(encodedValue: string): unknown {
  try {
    return JSON.parse(decodeURIComponent(encodedValue)) as unknown;
  } catch {
    return undefined;
  }
}

function parseBooleanOverride(encodedValue: string): boolean | undefined {
  const value = parseOverride(encodedValue);
  return typeof value === 'boolean' ? value : undefined;
}

function parseStringOverride(encodedValue: string): string | undefined {
  const value = parseOverride(encodedValue);
  return typeof value === 'string' ? value : undefined;
}

function parseNumberOverride(encodedValue: string): number | undefined {
  const value = parseOverride(encodedValue);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function parseStringListOverride(encodedValue: string): Array<string> | undefined {
  const list = parseOverride(encodedValue);
  return Array.isArray(list) && list.every((item) => typeof item === 'string') ? list : undefined;
}

function parseNumberListOverride(encodedValue: string): Array<number> | undefined {
  const list = parseOverride(encodedValue);
  return Array.isArray(list) && list.every((item) => typeof item === 'number' && Number.isFinite(item))
    ? list
    : undefined;
}

export function parseFrontendConfigOverride(
  type: FrontendConfigType,
  encodedValue: string,
): FrontendConfigValue | undefined {
  switch (type) {
    case 'Boolean':
      return parseBooleanOverride(encodedValue);
    case 'String':
      return parseStringOverride(encodedValue);
    case 'Number':
      return parseNumberOverride(encodedValue);
    case 'StringCsv':
      return parseStringListOverride(encodedValue);
    case 'NumberCsv':
      return parseNumberListOverride(encodedValue);
  }
}

export function parseFrontendConfigOverrideCookie(
  cookieName: string,
  cookieValue: string,
): FrontendConfigOverride | undefined {
  for (const type of CONFIG_TYPES) {
    const prefix = getFrontendConfigOverrideCookieName(type, '');
    if (cookieName.startsWith(prefix)) {
      const name = cookieName.slice(prefix.length);
      const value = parseFrontendConfigOverride(type, cookieValue);
      return value === undefined ? undefined : { type, name, value };
    }
  }

  if (cookieName.startsWith(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX)) {
    const value = parseBooleanOverride(cookieValue);
    return value === undefined
      ? undefined
      : {
          type: 'Boolean',
          name: cookieName.slice(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX.length),
          value,
        };
  }

  return undefined;
}

function getCookieValue(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookie = document.cookie.split(';').find((item) => item.trim().startsWith(`${name}=`));
  return cookie?.trim().slice(name.length + 1);
}

const URL_OVERRIDE_RESET_VALUE = 'reset';

const parseSearchParams = memoize((search: string) => new URLSearchParams(search));

function getUrlSearchParams(): URLSearchParams | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined;
  }
  return parseSearchParams(window.location.search);
}

function expireCookieIfPresent(name: string): void {
  if (getCookieValue(name) !== undefined) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

// Read-before-write keeps render-time reads cheap and self-heals if the cookie is
// cleared mid-session while the param is still in the URL.
function persistOverrideCookie(name: string, value: string): void {
  if (getCookieValue(name) !== value) {
    document.cookie = `${name}=${value}; max-age=${FRONTEND_CONFIG_OVERRIDE_MAX_AGE_SECONDS}; path=/`;
  }
}

// Cookie name -> value for the boolean overrides the current URL is setting, covering both
// the typed and legacy cookie formats so neither gets expired out from under the URL. Reads
// each name through params.get() so duplicated params (`?flag=false&flag=true`) resolve to
// the same value getBooleanUrlOverride applies, rather than to the last one iterated.
function getUrlOwnedBooleanOverrides(params: URLSearchParams): Map<string, string> {
  const owned = new Map<string, string>();
  for (const name of new Set(params.keys())) {
    const value = params.get(name);
    if (name !== FRONTEND_CONFIG_RESET_ALL_PARAM && (value === 'true' || value === 'false')) {
      const serialized = serializeFrontendConfigOverride('Boolean', value === 'true');
      owned.set(getFrontendConfigOverrideCookieName('Boolean', name), serialized);
      owned.set(`${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${name}`, serialized);
    }
  }
  return owned;
}

// The sweep rewrites overrides the URL itself is setting (in either cookie format) instead
// of expiring them, so a URL-set flag survives navigation even when no getter reads it on
// this page. Only existing cookies are rewritten — minting one for every `true`/`false`
// param would turn unrelated app params like `?debug=true` into 24h overrides; real flags
// still persist lazily as they are read.
function clearAllOverrideCookies(params: URLSearchParams): void {
  const urlOwned = getUrlOwnedBooleanOverrides(params);
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0]?.trim();
    if (!name?.startsWith(FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX)) {
      return;
    }

    const urlValue = urlOwned.get(name);
    if (urlValue === undefined) {
      expireCookieIfPresent(name);
    } else {
      persistOverrideCookie(name, urlValue);
    }
  });
}

// Fires once per page load (module scope, below): query params only change on full
// navigations, so per-getter re-checks would be redundant. The getUrlSearchParams guard
// makes this a no-op without a browser (SSR-safe).
function applyResetAllFromUrl(): void {
  const params = getUrlSearchParams();
  if (params?.get(FRONTEND_CONFIG_RESET_ALL_PARAM) === 'true') {
    clearAllOverrideCookies(params);
  }
}

applyResetAllFromUrl();

/**
 * URL-param override layer for boolean config — enables sharing a link that forces a flag
 * without the rjs-ext extension: `?<name>=true|false` applies the override and persists it
 * as the standard 24h override cookie, `?<name>=reset` clears that flag's override cookies,
 * and `?fc_reset_all=true` clears every override cookie.
 */
function getBooleanUrlOverride(name: string): boolean | undefined {
  const params = getUrlSearchParams();
  if (!params) {
    return undefined;
  }

  // Reserved control param — never a flag override, or reset-all would re-persist it.
  if (name === FRONTEND_CONFIG_RESET_ALL_PARAM) {
    return undefined;
  }

  const rawValue = params.get(name);
  if (rawValue === null) {
    return undefined;
  }

  if (rawValue === URL_OVERRIDE_RESET_VALUE) {
    // Expire both cookie formats so the read below falls through to DataRouter/default.
    expireCookieIfPresent(getFrontendConfigOverrideCookieName('Boolean', name));
    expireCookieIfPresent(`${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${name}`);
    return undefined;
  }

  if (rawValue !== 'true' && rawValue !== 'false') {
    return undefined;
  }

  const value = rawValue === 'true';
  persistOverrideCookie(
    getFrontendConfigOverrideCookieName('Boolean', name),
    serializeFrontendConfigOverride('Boolean', value),
  );
  return value;
}

export function getBooleanOverride(name: string): boolean | undefined {
  const urlValue = getBooleanUrlOverride(name);
  if (urlValue !== undefined) {
    return urlValue;
  }

  const typedValue = getCookieValue(getFrontendConfigOverrideCookieName('Boolean', name));
  if (typedValue !== undefined) {
    const override = parseBooleanOverride(typedValue);
    if (override !== undefined) {
      return override;
    }
  }

  const legacyValue = getCookieValue(`${FRONTEND_CONFIG_OVERRIDE_COOKIE_PREFIX}${name}`);
  return legacyValue === undefined ? undefined : parseBooleanOverride(legacyValue);
}

export function getStringOverride(name: string): string | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('String', name));
  return value === undefined ? undefined : parseStringOverride(value);
}

export function getNumberOverride(name: string): number | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('Number', name));
  return value === undefined ? undefined : parseNumberOverride(value);
}

export function getStringListOverride(name: string): Array<string> | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('StringCsv', name));
  return value === undefined ? undefined : parseStringListOverride(value);
}

export function getNumberListOverride(name: string): Array<number> | undefined {
  const value = getCookieValue(getFrontendConfigOverrideCookieName('NumberCsv', name));
  return value === undefined ? undefined : parseNumberListOverride(value);
}
